import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Link2, RefreshCw, Send, Wallet } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        <p className="mt-2 text-xs text-text-muted">{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
    </div>
  </div>
);

const pill = (s) => s === 'paid'
  ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200'
  : 'border-amber-400/20 bg-amber-500/10 text-amber-200';

export const PaymentLinksPanel = ({ authFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ client_id: 'neuralia369', concept: '', amount: '', currency: 'pen', customer_name: '', customer_wa: '' });

  const load = useCallback(async (notify = false) => {
    try {
      setLoading(true);
      const r = await authFetch(`${API}/results/payment-links/summary`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'No se pudo cargar links de pago');
      setData(j);
      if (notify) toast.success('Links de pago actualizados');
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const createLink = async (e) => {
    e.preventDefault();
    if (!form.concept || !form.amount) return toast.error('Concepto y monto son obligatorios');
    try {
      setBusy('create');
      const r = await authFetch(`${API}/results/payment-links`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: Number(form.amount), customer_name: form.customer_name || null, customer_wa: form.customer_wa || null, send_now: !!form.customer_wa }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error creando link');
      toast.success(`Link creado: ${fmt(j.amount_pen)}`);
      await navigator.clipboard?.writeText(j.url).catch(() => {});
      setForm((f) => ({ ...f, concept: '', amount: '', customer_name: '', customer_wa: '' }));
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };

  const action = async (id, path, okMsg) => {
    try {
      setBusy(id + path);
      const r = await authFetch(`${API}/results/payment-links/${id}/${path}`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error');
      toast.success(okMsg(j));
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };

  const t = data?.totals || {};
  return (
    <section className="space-y-6" data-testid="payment-links-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Fase 6 · Cobranza en soles</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Links de pago por cliente</h3>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="payment-links-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Stat icon={Wallet} label="Pendiente de cobro" value={fmt(t.pending_pen)} helper={`${t.pending_count || 0} link(s) abiertos`} testId="payment-links-total-pending" />
        <Stat icon={CheckCircle2} label="Cobrado" value={fmt(t.paid_pen)} helper={`${t.paid_count || 0} link(s) pagados`} testId="payment-links-total-paid" />
        <Stat icon={Link2} label="Clientes con links" value={data?.clients?.length || 0} helper={`TC referencial 1 USD = S/ ${data?.usd_pen_rate || 3.75}`} testId="payment-links-total-clients" />
      </div>

      <form onSubmit={createLink} className="glass-card grid gap-3 p-5 md:grid-cols-6" data-testid="payment-link-form">
        <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="client_id" data-testid="payment-link-client" className="rounded-xl border border-border-subtle bg-background/60 px-3 py-2 text-sm text-text-heading" />
        <input value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} placeholder="Concepto (ej. Plan Activa mes 1)" data-testid="payment-link-concept" className="rounded-xl border border-border-subtle bg-background/60 px-3 py-2 text-sm text-text-heading md:col-span-2" />
        <div className="flex gap-2">
          <input type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Monto" data-testid="payment-link-amount" className="w-full rounded-xl border border-border-subtle bg-background/60 px-3 py-2 text-sm text-text-heading" />
          <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="payment-link-currency" className="rounded-xl border border-border-subtle bg-background/60 px-2 py-2 text-sm text-text-heading">
            <option value="pen">S/</option><option value="usd">USD</option>
          </select>
        </div>
        <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Cliente" data-testid="payment-link-customer" className="rounded-xl border border-border-subtle bg-background/60 px-3 py-2 text-sm text-text-heading" />
        <div className="flex gap-2">
          <input value={form.customer_wa} onChange={(e) => setForm({ ...form, customer_wa: e.target.value })} placeholder="+51 WhatsApp (opcional)" data-testid="payment-link-wa" className="w-full rounded-xl border border-border-subtle bg-background/60 px-3 py-2 text-sm text-text-heading" />
          <button type="submit" disabled={busy === 'create'} data-testid="payment-link-create" className="inline-flex items-center gap-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-50">
            <Link2 className="h-4 w-4" /> Crear
          </button>
        </div>
      </form>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="payment-links-clients">
        {(data?.clients || []).map((c) => (
          <div key={c.client_id} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`payment-links-client-${c.client_id}`}>
            <p className="truncate text-sm font-semibold text-text-heading">{c.client_id}</p>
            <div className="mt-3 flex justify-between text-xs text-text-muted">
              <span>Pendiente</span><span className="font-semibold text-amber-200">{fmt(c.pending_pen)} · {c.pending_count}</span>
            </div>
            <div className="mt-1 flex justify-between text-xs text-text-muted">
              <span>Cobrado</span><span className="font-semibold text-emerald-200">{fmt(c.paid_pen)} · {c.paid_count}</span>
            </div>
          </div>
        ))}
        {!loading && !(data?.clients || []).length && <p className="text-sm text-text-muted" data-testid="payment-links-empty">Aún no hay links de pago. Crea el primero arriba o dile a Aynara por WhatsApp: "cobra 490 soles a Carlos".</p>}
      </div>

      <div className="glass-card overflow-hidden p-0">
        <table className="w-full text-left text-sm" data-testid="payment-links-table">
          <thead className="text-[10px] uppercase tracking-[0.18em] text-text-muted">
            <tr><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Concepto</th><th className="px-4 py-3">Monto</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Acciones</th></tr>
          </thead>
          <tbody>
            {(data?.recent || []).map((l) => (
              <tr key={l.link_id} className="border-t border-border-subtle/60" data-testid={`payment-link-row-${l.link_id}`}>
                <td className="px-4 py-3 text-text-muted">{l.client_id}<br /><span className="text-xs">{l.customer_name || '—'}</span></td>
                <td className="px-4 py-3 text-text-heading"><a href={l.url} target="_blank" rel="noreferrer" className="underline decoration-primary/40 hover:text-primary">{l.concept}</a>{l.simulated && <span className="ml-2 text-[10px] text-text-muted">SIM</span>}</td>
                <td className="px-4 py-3 font-semibold text-text-heading">{fmt(l.amount_pen)}</td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${pill(l.status)}`}>{l.status === 'paid' ? 'Pagado' : 'Pendiente'}</span></td>
                <td className="px-4 py-3">
                  {l.status !== 'paid' && (
                    <div className="flex gap-2">
                      {l.customer_wa && <button type="button" onClick={() => action(l.link_id, 'send', () => 'Link enviado por WhatsApp')} disabled={!!busy} data-testid={`payment-link-send-${l.link_id}`} className="rounded-full border border-border-subtle p-1.5 text-text-muted hover:text-primary" title="Enviar por WA"><Send className="h-3.5 w-3.5" /></button>}
                      <button type="button" onClick={() => action(l.link_id, 'sync', (j) => (j.status === 'paid' ? 'Pago confirmado en Stripe' : 'Sin pago aún en Stripe'))} disabled={!!busy} data-testid={`payment-link-sync-${l.link_id}`} className="rounded-full border border-border-subtle p-1.5 text-text-muted hover:text-primary" title="Sincronizar Stripe"><RefreshCw className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => action(l.link_id, 'mark-paid', () => 'Marcado como pagado')} disabled={!!busy} data-testid={`payment-link-markpaid-${l.link_id}`} className="rounded-full border border-emerald-400/30 p-1.5 text-emerald-200 hover:bg-emerald-500/10" title="Marcar pagado"><CheckCircle2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PaymentLinksPanel;
